import { COUNTRIES } from '../../defaultContent';
import type { Page } from '../../types';
import { createFraudEnablerQuestions } from './fraudEnablers';

const addPreventedActual: Page[] = [
  {
    pageType: 'question',
    title: 'Prevented Actuals',
    name: 'prevented-actual',
    description:
      'The Prevented Actuals section refers to instances of payments prevented from being processed due to the detection of fraud, where the value of the payment prevented can be evidenced with certainty.' +
      '<div class="govuk-!-margin-bottom-6"></div>',
    questions: [
      {
        questionType: 'radio',
        title: 'Do you have any instances of prevented actuals to declare?',
        name: 'prevented_actual_declare',
        options: ['Yes', 'No'],
        rules: {
          required: true,
        },
      },
    ],
  },
  {
    pageType: 'list',
    title: 'Add an instance of prevented actuals',
    name: 'prevented-actual-add-instance',
    type: 'nilreturn',
    fastToggle: true,
    total: {
      columns: [
        { title: 'Number of instances', field: 'prevented_actual_impact' },
        { title: 'Total Value', field: 'prevented_actual' },
      ],
    },
    description:
      'Prevented values should be evidenced with certainty. Prevented actuals should only refer to instances of fraud prevented, not error.',
    disabled: [
      {
        field: 'prevented_actual_declare',
        condition: 'equals', // optional
        value: 'No',
      },
    ],
    questions: [
      {
        questionType: 'select',
        title: 'Which spending area was the fraud prevented in?',
        name: 'prevented_actual_area',
        detailsHeading: 'Help with spending areas',
        detailsContent:
          ' <b>Benefits:</b> money given by the government to support members of the public' +
          ' <br/><b>Charges & other non-tax income:</b> money owed to public sector organisations (for example, court fines or prescription fees)' +
          ' <br/><b>Grants:</b> money given by a public sector organisation for a specific purpose' +
          ' <br/><b>Licences and permits:</b> licences, permits or certificates that grant permissions (for example, a Blue Badge, Surveyor licence, marriage certificate)' +
          ' <br/><b>Loans & credit guarantees:</b> money loaned or guaranteed by a public sector organisation' +
          ' <br/><b>Personnel:</b> employees (including contractors and agency workers)' +
          ' <br/><b>Procurement:</b> goods or services bought by public sector organisations (including suppliers)' +
          ' <br/><b>Special payments:</b> money given in unique or specific circumstances (for example, compensation)' +
          ' <br/><b>Subsidies:</b> support or incentives given by the government' +
          ' <br/><b>Tax & duties:</b> mandatory government charge for goods, income or services',
        options: [
          'Benefits',
          'Charges & other non-tax income',
          'Grants',
          'Licences and permits',
          'Loans & credit guarantees',
          'Personnel',
          'Procurement',
          'Special payments',
          'Subsidies',
          'Tax & duties',
        ],
        hintOption: 'Please Select',
        rules: { required: true },
      },
      {
        name: 'prevented_actual_deception',
        questionType: 'radio',
        title: 'What was the general nature of deception?',
        detailsHeading: 'Help with general nature of deception',
        detailsContent:
          ' <b>Abuse of position:</b> use of a person&apos;s position or role in the government or public sector' +
          ' <br/><b>Failure to declare/disclose:</b> Intentionally omitting information you legally need to share' +
          ' <br/><b>False representation:</b> intentionally hiding or giving misleading information' +
          ' <br/><b>Unknown:</b> I don&apos;t know how the deception was committed',
        options: [
          {
            option: 'Abuse of position',
            questions: [
              {
                questionType: 'select',
                title: 'Specific nature of deception',
                detailsHeading: 'Help with specific nature of deception',
                detailsContent:
                  '<b>Allowances:</b> abusing authority to make allowance payments' +
                  '<br/><b>Application for employment:</b> abusing authority to recruit an unqualified candidate for personal benefit' +
                  '<br/><b>Asset misappropriation:</b> misuse of government property (for example, a laptop or phone)' +
                  '<br/><b>Contract rigging:</b> bidding for or being awarded a contract using false information' +
                  '<br/><b>Falsified application:</b> using internal access when applying for HMG Services (for example, a passport or student loan)' +
                  '<br/><b>Insider theft of assets:</b> theft of government property (for example, a laptop or phone)' +
                  '<br/><b>Mandate fraud:</b> changing a payment mandate (for example, details for a standing order)' +
                  '<br/><b>Misuse of purchasing card:</b> using corporate card details to buy unauthorised goods or services e.g GPC card, fuel card' +
                  '<br/><b>Payment fraud:</b> creating or diverting payments (for example, false or duplicate invoices)',
                name: 'prevented_actual_positions',
                hintOption: 'Please Select',
                options: [
                  'Allowances',
                  'Application for employment',
                  'Asset misappropriation',
                  'Contract rigging',
                  'Falsified application',
                  'Insider theft of assets',
                  'Mandate fraud',
                  'Misuse of purchasing card',
                  'Payment fraud',
                ],
                rules: {
                  required: true,
                },
              },
            ],
          },
          {
            option: 'Failure to declare/disclose',
            questions: [
              {
                questionType: 'select',
                title: 'Specific nature of deception',
                detailsHeading: 'Help with specific nature of deception',
                detailsContent:
                  '<b>Abuse of working hours:</b> omitting relevant information that would affect eligibility to continue claiming flexi-time or annual leave' +
                  '<br/><b>Allowances:</b> omitting relevant information in continue claiming for government allowances' +
                  '<br/><b>Application for employment:</b> omitting relevant information to continue working in a role (for example, no longer holding a professional certification)' +
                  '<br/><b>Asset misappropriation:</b> not disclosing information that facilitates misuse of government property (for example, moving abroad and continuing to use a laptop or phone)' +
                  '<br/><b>Change of circumstances:</b> not disclosing a change of circumstances that affects eligibility for a service, contract or application' +
                  '<br/><b>Charge evasion:</b> omitting relevant information to evade paying money (for example, fees, taxes or fines)' +
                  '<br/><b>Expense claims:</b> omitting relevant information in continue claiming for government expenses (for example, travel or food)' +
                  '<br/><b>Misuse of funding:</b> not disclosing using funds outside the conditions of the agreement',
                name: 'prevented_actual_disclose',
                hintOption: 'Please Select',
                options: [
                  'Abuse of working hours',
                  'Allowances',
                  'Application for employment',
                  'Asset misappropriation',
                  'Change of circumstances',
                  'Charge evasion',
                  'Expense claims',
                  'Misuse of funding',
                ],
                rules: {
                  required: true,
                },
              },
            ],
          },
          {
            option: 'False representation',
            questions: [
              {
                questionType: 'select',
                title: 'Specific nature of deception',
                detailsHeading: 'Help with specific nature of deception',
                detailsContent:
                  '<b>Abuse of working hours:</b> falsely claiming time worked (for example, flexi-time or annual leave)' +
                  '<br/><b>Allowances:</b> falsely claiming on government allowances' +
                  '<br/><b>Application for employment:</b> using misleading information to get a role (for example, employment history or qualifications)' +
                  '<br/><b>Asset misappropriation:</b> misuse of government property (for example, a laptop or phone)' +
                  '<br/><b>Charge evasion:</b> giving misleading information to evade paying money (for example, fees, taxes or fines)' +
                  '<br/><b>Expense claims:</b> falsely claiming on government expenses (for example, travel or food)' +
                  '<br/><b>Falsified application:</b> using misleading information when applying for HMG Services (for example, a passport or student loan)' +
                  '<br/><b>Falsified invoice:</b> giving misleading information on an invoice (for example, inflating costs)' +
                  '<br/><b>Impersonation of purchasing cardholder:</b> use of corporate card details through impersonation of a public sector employee or official' +
                  '<br/><b>Mandate fraud:</b> changing a payment mandate (for example, details for a standing order)' +
                  '<br/><b>Misuse of funding:</b> using funds outside the conditions of the agreement' +
                  '<br/><b>Product substitution or misselling:</b> using or providing a different product or service than promised or misrepresenting the need for a product or service',
                name: 'prevented_actual_representation',
                hintOption: 'Please Select',
                options: [
                  'Abuse of working hours',
                  'Allowances',
                  'Application for employment',
                  'Asset misappropriation',
                  'Charge evasion',
                  'Expense claims',
                  'Falsified application',
                  'Falsified invoice',
                  'Impersonation of purchasing card holder',
                  'Mandate fraud',
                  'Misuse of funding',
                  'Product substitution or misselling',
                ],
                rules: {
                  required: true,
                },
              },
            ],
          },
          {
            option: 'Unknown',
            questions: [
              {
                questionType: 'select',
                title: 'Specific nature of deception',
                detailsHeading: 'Help with specific nature of deception',
                detailsContent: "I don't know how the deception was committed",
                name: 'prevented_actual_unknown',
                hintOption: 'Please Select',
                options: ['Unknown'],
                rules: {
                  required: true,
                },
              },
            ],
          },
        ],
        rules: {
          required: true,
        },
      },
      ...createFraudEnablerQuestions('prevented_actual_support'),
      {
        questionType: 'radio',
        title: 'Is this related to Covid 19 activity?',
        name: 'prevented_actual_c19',
        options: ['Yes', 'No'],
        rules: { required: true },
      },
      {
        questionType: 'instanceInput',
        title: 'How many instances were prevented?',
        name: 'prevented_actual_impact',
        txt: `Total number of instances.`,
        rules: {
          required: true,
        },
      },
      {
        questionType: 'costInput',
        displaySeperator: 'thousand',
        title: 'What was the value of the prevented fraud?',
        name: 'prevented_actual',
        txt: `Total value`,
        rules: {
          required: true,
          decimal: 'decimal(2)',
        },
      },
      {
        questionType: 'textarea',
        title: 'Comments',
        hideOptionalLabel: true,
        name: 'prevented_actual_comments',
        hint: `Please ensure you do not include any personal or sensitive information`,
        rules: {
          required: false,
        },
      },
    ],
  },
];
export default addPreventedActual;
